import {
  composeClassNames,
  ifDefined,
  universalEach,
  withDefault,
} from "@faedev/editor-utils";
import { useRouter } from "next/router";
import { CSSProperties, ReactNode } from "react";
import Slider from "react-slick";
import SliderArrow from "src/components/SliderArrow";
import Translate from "src/components/Translate";
import VideoCard from "src/components/VideoCard";
import constants from "src/constants";
import validateContent from "src/functions/validateContent";
import { sVideoList } from "src/styles/sVideoList.module.scss";
import { ApiVideoCard } from "src/types/ApiVideoCard";
import { VariantLectureType } from "src/types/VariantLectureType";
import { VariantProgramType } from "src/types/VariantProgramType";
import { VariantSize } from "src/types/VariantSize";
import { VariantVideoPreview } from "src/types/VariantVideoPreview";

type Props = {
  size: VariantSize;
  title?: ReactNode | null | undefined;
  data: ApiVideoCard[];
  contentType?: VariantLectureType | VariantProgramType;
  type: VariantVideoPreview;
  style?: CSSProperties | null | undefined;
  theme?: "light" | "dark";
  className?: (string | string) | null | undefined;
};

function VideoListSlider({
  size,
  data,
  type,
  style,
  className,
  theme,
  contentType,
}: Props) {
  const { query } = useRouter();
  return data && Array.isArray(data) && data.length > 0 ? (
    <div
      style={style}
      className={composeClassNames([
        sVideoList,
        theme,
        className,
        "video-list-wrapper",
      ])}
    >
      {data.length === 0 ? (
        <Translate tKey={"No videos found"} />
      ) : (
        <Slider
          arrows={true}
          infinite={true}
          speed={500}
          nextArrow={<SliderArrow direction={`next`} />}
          prevArrow={<SliderArrow direction={`prev`} />}
          slidesToShow={3}
          slidesToScroll={3}
          responsive={[
            {
              breakpoint: constants.Breakpoints.xl,
              settings: { slidesToShow: 3, slidesToScroll: 3 },
            },
            {
              breakpoint: constants.Breakpoints.lg,
              settings: { slidesToShow: 2, slidesToScroll: 2 },
            },
            {
              breakpoint: constants.Breakpoints.md,
              settings: { slidesToShow: 1, slidesToScroll: 1 },
            },
            {
              breakpoint: constants.Breakpoints.sm,
              settings: { slidesToShow: 1, slidesToScroll: 1 },
            },
          ]}
        >
          {(() => {
            let validated = false;
            let sortData;

            const classesType = () => {
              switch (query.type) {
                case `relax`:
                  return constants.List.hpClassesRelax;
                case `health`:
                  return constants.List.hpClassesHealth;
                case `gravid`:
                  return constants.List.hpClassesGravid;
                case `kids`:
                  return constants.List.hpClassesKids;
                default:
                  return constants.List.hpClasses;
              }
            };

            const seriesType = () => {
              switch (query.type) {
                case `relax`:
                  return constants.List.hpSeriesRelax;
                case `health`:
                  return constants.List.hpSeriesHealth;
                case `gravid`:
                  return constants.List.hpSeriesGravid;
                case `kids`:
                  return constants.List.hpSeriesKids;
                default:
                  return constants.List.hpSeries;
              }
            };

            const classes = classesType();
            const series = seriesType();
            const festivals = constants.List.hpFestivals;
            const streams = constants.List.hpAudios;
            const audios = constants.List.hpAudios;

            const sortedSeries = series.map(
              (seriePosition) =>
                data &&
                Array.isArray(data) &&
                data.find((item) => Number(item.id) === seriePosition)
            );
            const sortedClasses = classes.map(
              (classPosition) =>
                data &&
                Array.isArray(data) &&
                data.find((item) => Number(item.id) === classPosition)
            );
            const sortedFestivals = festivals.map(
              (festivalPosition) =>
                data &&
                Array.isArray(data) &&
                data.find((item) => Number(item.id) === festivalPosition)
            );
            const sortedStreams = streams.map(
              (streamPosition) =>
                data &&
                Array.isArray(data) &&
                data.find((item) => Number(item.id) === streamPosition)
            );
            const sortedAudios = audios.map(
              (audioPosition) =>
                data &&
                Array.isArray(data) &&
                data.find((item) => Number(item.id) === audioPosition)
            );

            switch (contentType) {
              case "serie":
                sortData = sortedSeries;
                break;
              case "class":
                sortData = sortedClasses;
                break;
              case "festival":
                sortData = sortedFestivals;
                break;
              case "stream":
                sortData = sortedStreams;
                break;
              case "audio":
                sortData = sortedAudios;
                break;
              default:
                sortData = sortedClasses;
            }

            return universalEach(sortData, (item) => {
              validated = validateContent(
                withDefault(item?.blocked, true),
                ifDefined(
                  item?.verified,
                  (value) => value.verified,
                  () => false
                )
              );
              return (
                validated && (
                  <VideoCard
                    data={item}
                    type={withDefault(type, `preview`)}
                    size={size}
                    bunch={data.length}
                    key={`video-card-` + item.id}
                  />
                )
              );
            });
          })()}
        </Slider>
      )}
    </div>
  ) : null;
}

export default VideoListSlider;
